import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const DocumentationPage = () => {
  const { page } = useParams();
  const navigate = useNavigate();
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDocumentation = async () => {
      setLoading(true);
      try {
        // Determine which HTML file to load based on the page parameter
        let fileName = 'index.html';
        if (page) {
          switch (page) {
            case 'user-guide':
              fileName = 'user-guide.html';
              break;
            case 'admin-guide':
              fileName = 'admin-guide.html';
              break;
            case 'quick-reference':
              fileName = 'quick-reference.html';
              break;
            case 'faq':
              fileName = 'faq.html';
              break;
            default:
              fileName = 'index.html';
          }
        }

        // Fetch the HTML content from the public/docs directory
        const response = await fetch(`/docs/${fileName}`);
        if (!response.ok) {
          throw new Error('Documentation not found');
        }
        
        const html = await response.text();
        setHtmlContent(html);
      } catch (error) {
        console.error('Error loading documentation:', error);
        setHtmlContent(`
          <div style="padding: 40px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            <h1 style="color: #2d3748; margin-bottom: 20px;">Documentation Not Found</h1>
            <p style="color: #4a5568; margin-bottom: 30px;">The requested documentation page could not be loaded.</p>
            <button onclick="window.history.back()" style="padding: 10px 20px; background: #667eea; color: white; border: none; border-radius: 8px; cursor: pointer;">
              Go Back
            </button>
          </div>
        `);
      } finally {
        setLoading(false);
      }
    };

    loadDocumentation();
  }, [page]);

  useEffect(() => {
    if (!loading && htmlContent) {
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;

      // Extract the body content
      const body = tempDiv.querySelector('body');
      if (body) {
        // Get the content without the body tag
        const bodyContent = body.innerHTML;
        
        // Update the content state with just the body content
        setHtmlContent(bodyContent);
      }
    }
  }, [loading, htmlContent]);

  useEffect(() => {
    // Add navigation functionality after content is loaded
    if (!loading && htmlContent) {
      // Add navigation functionality for back links
      const backLinks = document.querySelectorAll('.back-link');
      backLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          navigate('/documentation');
        });
      });

      // Add internal navigation functionality
      const internalLinks = document.querySelectorAll('a[href^="#"]');
      internalLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const target = document.querySelector(link.getAttribute('href'));
          if (target) {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        });
      });

      // Add search functionality for FAQ page
      const searchInput = document.getElementById('searchInput');
      if (searchInput) {
        const faqItems = document.querySelectorAll('.faq-item');
        searchInput.addEventListener('input', function() {
          const searchTerm = this.value.toLowerCase();
          
          faqItems.forEach(item => {
            const question = item.querySelector('h4').textContent.toLowerCase();
            const answer = item.textContent.toLowerCase();
            
            if (question.includes(searchTerm) || answer.includes(searchTerm)) {
              item.classList.remove('hidden');
            } else {
              item.classList.add('hidden');
            }
          });
        });
      }
    }
  }, [htmlContent, loading, navigate]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#4a5568',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        Loading documentation...
      </div>
    );
  }

  return (
    <div 
      dangerouslySetInnerHTML={{ __html: htmlContent }}
      style={{ 
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    />
  );
};

export default DocumentationPage; 